import { Fragment, useMemo, type ReactNode } from 'react'
import { useRecordTitles } from '../data/queries'
import type { RelationSlot } from './relations'
import { useRelatedCounts } from './useRelations'
import s from './RecordFacts.module.css'

/**
 * The facts under a record's name (#2406, leading design: "Q8 · q8.com · BE ·
 * 9 connections").
 *
 * ── only what is GOVERNED ──────────────────────────────────────────────────
 * Two sources, both already answering other surfaces:
 *
 *   · the entity's DECLARED subtitle — `core.titles_of`, the same door every
 *     list, pin and recent names this record by. An entity that declares none
 *     contributes nothing; the line is not composed from whichever columns
 *     happen to look informative, because that is a second, unreviewed answer
 *     to "how is this record described";
 *   · the connection count the related slots measured — `core.related_counts`,
 *     summed over the same slots the Connections panel draws, so the header and
 *     the panel cannot say two different numbers. A capped or unanswered slot
 *     makes it "at least"; nothing counted at all makes it absent, never 0.
 *
 * `core_organization` declares a title and NO subtitle today, so on an
 * organization this line is the connection count alone until a subtitle is
 * declared (a core registration, not a console change).
 */
export function RecordFacts({ entity, id, slots }: { entity: string; id: string; slots: readonly RelationSlot[] }) {
  const refs = useMemo(() => [{ type: entity, id }], [entity, id])
  const titles = useRecordTitles(refs)
  const counts = useRelatedCounts(entity, id, slots.length > 0)

  const parts: { key: string; node: ReactNode }[] = []
  const subtitle = titles.subtitle(entity, id)
  if (subtitle) parts.push({ key: 'subtitle', node: <span>{subtitle}</span> })

  if (slots.length > 0 && !counts.isLoading && !counts.isError) {
    let n = 0
    let floor = false
    let answered = 0
    for (const slot of slots) {
      const count = counts.bySlot.get(slot.id)
      if (!count) { floor = true; continue }
      answered += 1
      n += count.n
      if (count.capped) floor = true
    }
    if (answered > 0) {
      parts.push({
        key: 'connections',
        node: <span className="tnum" data-testid="record-facts-connections">
          {`${floor ? 'At least ' : ''}${n} ${n === 1 ? 'connection' : 'connections'}`}
        </span>,
      })
    }
  }

  if (parts.length === 0) return null
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 && <span className={s.sep} aria-hidden="true">·</span>}
          {part.node}
        </Fragment>
      ))}
    </>
  )
}
